import React from 'react';
import PropTypes from 'prop-types';
import { I18nContext, Config } from '@deporvillage/utils-library-component';

import AutocompleteList from './AutocompleteList.js';
import AutocompleteGrid from './AutocompleteGrid.js';

import styles from './Autocomplete.module.scss';

let delayId;

const cache = {};
const delayMs = 500;
const inputThreshold = 3;

const ProductLimit = {
    Mobile: 10,
    Desktop: 7,
};

const Autocomplete = ({ onSubmit, onClick, onChange, onOpen }) => {

    const { isMobile, api, lang, country } = React.useContext(Config);
    const countryCode = country.code.toLowerCase();
    const { translate } = React.useContext(I18nContext);
    const ref = React.useRef(null);
    const form = React.useRef(null);
    const input = React.useRef(null);
    const [collection, setCollection] = React.useState(null);

    const handleInputChange = (ev) => {

        let val = ev.target.value;

        if (cache[val]) return setCollection(cache[val]);

        if (val?.length < inputThreshold) {
            setCollection(null);
            return false;
        }

        /* eslint-disable-next-line no-undef */
        clearTimeout(delayId);

        /* istanbul ignore next */
        /* eslint-disable-next-line no-undef */
        delayId = setTimeout(() => {
            onChange && onChange(val);
            /* eslint-disable-next-line no-undef */
            fetch(`${api}/api/search/autocomplete/${lang}/${countryCode}?query=${val}&limit=${isMobile ? ProductLimit.Mobile : ProductLimit.Desktop}`)
                .then(ans => ans.json())
                /* istanbul ignore next */
                .catch(() => { })
                /* istanbul ignore next */
                .then((ans) => {
                    if (ans) {
                        cache[val] = ans;
                        setCollection(cache[val]);
                    }
                });
        }, delayMs);
    };

    const close = (ev) => {
        if (ev.type === 'keydown' && ev.keyCode !== 27) return;
        if (ref.current !== ev.target && ref.current.contains(ev.target)) return;
        setCollection(null);
    };

    const handleOnClick = (e) => onClick && onClick(e);

    const handleOnClose = (e) => {
        input.current.value = '';
        setCollection(null);
        onClick && onClick(e);
    };

    const handleOnSubmit = (ev) => {
        ev.preventDefault();
        if (!input.current.value) return;
        /* eslint-disable-next-line no-undef */
        form.current.submit && form.current.submit(new Event('submit'));
        onSubmit && onSubmit();
    };

    const handleOnSubmitButton = (ev, e) => {
        handleOnSubmit(ev);
        onClick && onClick(e);
    };

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('click', close);
        window.addEventListener('keydown', close);
        return () => {
            clearTimeout(delayId);
            window.removeEventListener('click', close);
            window.removeEventListener('keydown', close);
        };
        /* eslint-enable no-undef */
    }, [collection]);

    React.useEffect(() => {
        /* istanbul ignore next */
        onOpen && onOpen(collection && collection.items);
    }, [collection]);

    return (
        <div className={`${styles['autocomplete-component']} ${collection && collection.items ? styles[`is-open`] : ``}`} ref={ref}>
            <form
                ref={form}
                action={'/catalogsearch/result'}
                method={'get'}
                onSubmit={handleOnSubmit}
            >
                <input ref={input}
                    data-testid={'autocomplete-input-search'}
                    type={'search'}
                    name={'q'}
                    placeholder={translate('header.search.placeholder')}
                    autoComplete={'off'}
                    onInput={handleInputChange}
                    onFocus={handleInputChange}
                />
                <div className={styles['actions-wrapper']}>
                    <button type={'submit'} data-testid={'autocomplete-submit-button'}>
                        <i className={'icon icon-search'} />
                    </button>
                </div>
            </form>

            {
                !collection || !collection.items ? null : (
                    isMobile ?
                        <AutocompleteList
                            query={collection.searchString}
                            count={collection.itemsCount}
                            items={collection.items}
                            categories={collection.categories}
                            brands={collection.brands}
                            onClick={handleOnClick}
                            onSubmit={handleOnSubmitButton}
                            onClose={handleOnClose}
                        /> :
                        <AutocompleteGrid
                            query={collection.searchString}
                            count={collection.itemsCount}
                            items={collection.items}
                            categories={collection.categories}
                            brands={collection.brands}
                            onClick={handleOnClick}
                            onSubmit={handleOnSubmitButton}
                            onClose={handleOnClose}
                        />
                )
            }
        </div>
    );
};
Autocomplete.propTypes = {
    onClick: PropTypes.func,
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    onOpen: PropTypes.func,
};

export default Autocomplete;
